import MusicIcon from "@/assets/images/icons/music-icon.svg";
import ExternalLink from "@/components/ExternalLink";
import { SkillPathwayThumbnail } from "@/components/ui/SkillPathwayThumbnail";
import {
  skillPathwayHref,
  skillPathwayRoute,
} from "@/lib/skillPathwayNavigation";
import type { SkillPathway } from "@/supabase/services/optimizedCourse";
import { useRouter } from "expo-router";
import React from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
  type ViewStyle,
} from "react-native";
import { SizableText, XStack, YStack, useMedia } from "tamagui";
import { SectionTitle } from "@/components/ui/Typography";

const PILL_WIDTH_GTMD = 265;
const PILL_WIDTH_GTSM = 229;
const PILL_WIDTH_MOBILE = 180;
const PILL_HEIGHT = 56;
const PILL_HEIGHT_GTSM = 64;
const PILL_PADDING = 16;
const PILL_BORDER_RADIUS = 20;
const GAP = 16;
const TITLE_GAP = 12;

const PILL_BG = "#FFF";
const PILL_BOX_SHADOW_SOFT = "0 1px 6px rgba(0,0,0,0.08)";
const PILL_SHADOW_ORANGE = "#E17100";
const PILL_BOX_SHADOW_ORANGE = `0 2px 4px 0 ${PILL_SHADOW_ORANGE}, 0 0 2px 0 ${PILL_SHADOW_ORANGE}`;

type SkillShortcutsProps = {
  pathways: SkillPathway[];
  courseSlug: string;
  courseTitle: string;
};

function nativePillStyle(isFirstPill: boolean): ViewStyle {
  if (isFirstPill) {
    return {
      elevation: 0,
      borderWidth: 1,
      borderColor: "rgba(225, 113, 0, 0.3)",
      ...(Platform.OS === "ios"
        ? {
            shadowColor: PILL_SHADOW_ORANGE,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.08,
            shadowRadius: 2,
          }
        : {}),
    };
  }
  return {
    elevation: 0,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.06)",
    ...(Platform.OS === "ios"
      ? {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.03,
          shadowRadius: 2,
        }
      : {}),
  };
}

export function SkillShortcuts({
  pathways,
  courseSlug,
  courseTitle,
}: SkillShortcutsProps) {
  const router = useRouter();
  const media = useMedia();
  const { width: screenWidth } = useWindowDimensions();
  const isTablet = media.gtSm && !media.gtMd;

  const pillPaddingX = isTablet ? 16 : media.gtSm ? PILL_PADDING : 14;
  const pillPaddingY = isTablet ? 14 : media.gtSm ? PILL_PADDING : 12;
  const pillGap = isTablet ? 12 : 10;
  const iconInnerSize = 20;

  const pillWidth = media.gtMd
    ? PILL_WIDTH_GTMD
    : media.gtSm
      ? PILL_WIDTH_GTSM
      : PILL_WIDTH_MOBILE;

  const maxContentWidth = 1140;
  const basePadding = 16;
  const leftPadding =
    screenWidth > maxContentWidth
      ? (screenWidth - maxContentWidth) / 2 + basePadding
      : basePadding;

  const minGridWidth = 4 * pillWidth + 3 * GAP;

  if (!pathways.length) return null;

  const heading = pathways[0]?.section_title || "Skill Pathways";

  const minPosition = Math.min(...pathways.map((x) => x.position));
  const firstPathwayId = pathways.find((x) => x.position === minPosition)?.id;

  const renderPill = (p: SkillPathway) => {
    const isFirstPill = p.id === firstPathwayId;
    const url = skillPathwayHref(p.slug, courseSlug, courseTitle);
    const pillContent = (
      <YStack
        width={pillWidth}
        height={PILL_HEIGHT}
        $gtSm={{ height: PILL_HEIGHT_GTSM }}
        overflow="hidden"
        jc="center"
        br={PILL_BORDER_RADIUS}
        px={pillPaddingX}
        py={pillPaddingY}
        bg={PILL_BG}
        {...(Platform.OS === "web"
          ? {
              borderWidth: 0,
              elevation: 0,
              shadowOpacity: 0,
              style: {
                boxShadow: isFirstPill
                  ? PILL_BOX_SHADOW_ORANGE
                  : PILL_BOX_SHADOW_SOFT,
              } as object,
            }
          : { style: nativePillStyle(isFirstPill) })}
        cursor="pointer"
      >
        <XStack ai="center" jc="flex-start" gap={pillGap} w="100%" minWidth={0}>
          <YStack
            w={24}
            h={24}
            py={2}
            px={2}
            $gtSm={{ w: 36, h: 36, py: 2, px: 3 }}
            br={6}
            bg={p.color || "#FDE9DB"}
            jc="center"
            ai="center"
            flexShrink={0}
          >
            <SkillPathwayThumbnail
              uri={p.thumbnail}
              size={iconInnerSize}
              fallback={
                <MusicIcon
                  width={iconInnerSize}
                  height={iconInnerSize}
                  color="#5C5854"
                />
              }
            />
          </YStack>
          <SizableText
            flex={1}
            flexShrink={1}
            minWidth={0}
            size="$3"
            $gtSm={{ size: "$4", lineHeight: 24, letterSpacing: 0 }}
            {...(isTablet
              ? { fontSize: 14, lineHeight: 20, letterSpacing: 0 }
              : { fontSize: 13, lineHeight: 16, letterSpacing: 0 })}
            fontWeight="600"
            color="$color12"
            textAlign="left"
            numberOfLines={2}
          >
            {p.title}
          </SizableText>
        </XStack>
      </YStack>
    );

    if (Platform.OS === "web") {
      return (
        <ExternalLink key={p.id} url={url}>
          {pillContent}
        </ExternalLink>
      );
    }

    return (
      <Pressable
        key={p.id}
        onPress={() =>
          router.push(skillPathwayRoute(p.slug, courseSlug, courseTitle) as any)
        }
        style={({ pressed }) => ({
          width: pillWidth,
          alignSelf: "flex-start",
          opacity: pressed ? 0.92 : 1,
        })}
      >
        {pillContent}
      </Pressable>
    );
  };

  return (
    <YStack w="100%" py={media.gtSm ? 24 : 16} position="relative">
      <YStack w="100%" maw={1140} mx="auto" px={16}>
        <SectionTitle color="$color" mb={TITLE_GAP}>
          {heading}
        </SectionTitle>
      </YStack>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          paddingVertical: 4,
          paddingLeft: leftPadding,
          paddingRight: basePadding,
        }}
      >
        <YStack w={minGridWidth}>
          <XStack flexWrap="wrap" gap={GAP} rowGap={GAP}>
            {pathways.map((p) => renderPill(p))}
          </XStack>
        </YStack>
      </ScrollView>
    </YStack>
  );
}
